import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Modal,
  PageHeader,
  SelectField,
  SkeletonRows,
  TextField,
  useToast,
  type Column,
} from '@csa/ui';
import { api, ApiError } from '../../lib/api.js';

/**
 * Approval policy.
 *
 * Rules are evaluated in priority order and the first match wins; absent
 * fields in a match are wildcards. Both of those are stated on the page,
 * because a rules table where the semantics are implicit is a rules table
 * that somebody eventually misreads -- and the cost of misreading this one
 * is a production write going through unapproved.
 *
 * The important thing this page does *not* do is let anyone switch
 * maker-checker off. Data-modifying statements and mutating API calls always
 * route through approval, enforced in code rather than by a rule, so no edit
 * here can remove it. The rules decide *how many* approvers and for how
 * long, not whether.
 *
 * Every edit records both the prior and the new values, and an edit that
 * reduces a requirement raises a separate alerting event. Someone dropping a
 * production rule from two approvers to one surfaces the same day.
 */

interface ApprovalRule {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly enabled: boolean;
  readonly priority: number;
  readonly match: RuleMatch;
  readonly requiredApprovals: number;
  readonly ttlMinutes: number;
  readonly updatedAt: string;
}

interface RuleMatch {
  readonly environment?: string;
  readonly targetKind?: string;
  readonly targetKey?: string;
  readonly statementClass?: string;
  readonly httpMethod?: string;
  readonly credentialKind?: string;
  readonly mode?: string;
  readonly rowCountAbove?: number;
}

const BLANK: Omit<ApprovalRule, 'id' | 'updatedAt'> = {
  name: '',
  description: '',
  enabled: true,
  priority: 100,
  match: {},
  requiredApprovals: 1,
  ttlMinutes: 240,
};

export function ApprovalRulesPage(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Partial<ApprovalRule> | null>(null);

  const rules = useQuery({
    queryKey: ['approval-rules'],
    queryFn: () => api.get<{ rules: ApprovalRule[] }>('/api/admin/approval-rules'),
  });

  const save = useMutation({
    mutationFn: (rule: Partial<ApprovalRule>) => {
      const body = {
        name: rule.name,
        description: rule.description ?? '',
        enabled: rule.enabled ?? true,
        priority: rule.priority ?? 100,
        match: rule.match ?? {},
        requiredApprovals: rule.requiredApprovals ?? 1,
        ttlMinutes: rule.ttlMinutes ?? 240,
      };
      return rule.id
        ? api.patch(`/api/admin/approval-rules/${rule.id}`, body)
        : api.post('/api/admin/approval-rules', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-rules'] });
      setEditing(null);
      toast({ tone: 'success', title: 'Rule saved' });
    },
    onError: (error: unknown) =>
      toast({
        tone: 'danger',
        title: 'Could not save the rule',
        message: error instanceof ApiError ? error.message : String(error),
      }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/approval-rules/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-rules'] });
      toast({
        tone: 'warning',
        title: 'Rule deleted',
        message: 'Removing a rule always reduces coverage, so this raised an alert.',
      });
    },
  });

  const columns: Column<ApprovalRule>[] = [
    {
      key: 'priority',
      header: 'Priority',
      render: (rule) => <span className="csa-numeric font-mono text-xs">{rule.priority}</span>,
    },
    {
      key: 'name',
      header: 'Rule',
      render: (rule) => (
        <div className="flex flex-col">
          <span className={rule.enabled ? '' : 'line-through opacity-60'}>{rule.name}</span>
          <span className="text-xs text-subtle">{rule.description}</span>
        </div>
      ),
    },
    {
      key: 'match',
      header: 'Matches',
      render: (rule) => <MatchSummary match={rule.match} />,
    },
    {
      key: 'requiredApprovals',
      header: 'Approvers',
      render: (rule) => (
        <Badge tone={rule.requiredApprovals > 1 ? 'success' : 'neutral'}>
          {rule.requiredApprovals}
        </Badge>
      ),
    },
    {
      key: 'ttlMinutes',
      header: 'Expires after',
      render: (rule) => <span className="text-xs">{formatTtl(rule.ttlMinutes)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (rule) => (
        <div className="flex gap-1">
          <Button size="xs" variant="ghost" onClick={() => setEditing(rule)}>
            Edit
          </Button>
          <Button
            size="xs"
            variant="ghost"
            iconOnly
            aria-label={`Delete ${rule.name}`}
            onClick={() => remove.mutate(rule.id)}
          >
            <TrashIcon className="size-4" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  if (rules.isPending) return <SkeletonRows rows={6} />;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PageHeader
        title="Approval policy"
        description="Rules are tried in priority order and the first match wins. A field left blank matches anything."
        actions={
          <Button
            leadingIcon={<PlusIcon className="size-4" aria-hidden />}
            onClick={() => setEditing({ ...BLANK })}
          >
            New rule
          </Button>
        }
      />

      <Alert tone="info">
        Data-modifying statements and mutating API calls always require approval. That is enforced
        in code, not by a rule, so nothing configured here can remove it — these rules decide how
        many approvers and how long a request stays open.
      </Alert>

      {(rules.data?.rules.length ?? 0) === 0 ? (
        <EmptyState
          title="No rules are configured"
          description="Without a matching rule, only the always-approved actions route through maker-checker. Add a rule for production reads or large exports."
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rules.data!.rules}
          rowKey={(rule) => rule.id}
          caption="Approval rules in evaluation order"
        />
      )}

      {editing ? (
        <RuleEditor
          rule={editing}
          onChange={setEditing}
          onSave={() => save.mutate(editing)}
          onClose={() => setEditing(null)}
          pending={save.isPending}
        />
      ) : null}
    </div>
  );
}

function MatchSummary({ match }: { match: RuleMatch }): ReactElement {
  const parts = Object.entries(match).filter(([, value]) => value !== undefined && value !== '');

  if (parts.length === 0) {
    return (
      // A rule matching everything is legitimate as a catch-all at low
      // priority, but at high priority it shadows every rule below it. Said
      // plainly rather than left as an empty cell.
      <Badge tone="warning">everything</Badge>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {parts.map(([field, value]) => (
        <code key={field} className="rounded bg-sunken px-1.5 py-0.5 font-mono text-xs">
          {field}={String(value)}
        </code>
      ))}
    </div>
  );
}

function RuleEditor({
  rule,
  onChange,
  onSave,
  onClose,
  pending,
}: {
  rule: Partial<ApprovalRule>;
  onChange: (rule: Partial<ApprovalRule>) => void;
  onSave: () => void;
  onClose: () => void;
  pending: boolean;
}): ReactElement {
  const match = rule.match ?? {};
  const setMatch = (patch: Partial<RuleMatch>): void => {
    // An empty string means "unset", i.e. a wildcard, so it is removed from
    // the object rather than stored. A stored empty string would be a match
    // against the literal empty value and would never fire.
    const next: Record<string, unknown> = { ...match, ...patch };
    for (const [key, value] of Object.entries(next)) {
      if (value === '' || value === undefined) delete next[key];
    }
    onChange({ ...rule, match: next as RuleMatch });
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={rule.id ? `Edit ${rule.name}` : 'New approval rule'}
      description="All the fields under “Matches” must hold for the rule to fire. Leave a field blank to match anything."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} loading={pending} disabled={(rule.name ?? '').trim().length < 3}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-3">
          <TextField
            label="Name"
            containerClassName="min-w-64 flex-1"
            value={rule.name ?? ''}
            onChange={(event) => onChange({ ...rule, name: event.target.value })}
            placeholder="Production writes need two approvers"
          />
          <TextField
            label="Priority"
            type="number"
            containerClassName="w-32"
            hint="Lower runs first."
            value={String(rule.priority ?? 100)}
            onChange={(event) => onChange({ ...rule, priority: Number(event.target.value) })}
          />
        </div>

        <TextField
          label="Description"
          value={rule.description ?? ''}
          onChange={(event) => onChange({ ...rule, description: event.target.value })}
          hint="Shown to the requester as the reason their request needs approving."
          placeholder="Any statement that changes production data"
        />

        <fieldset className="rounded-md border border-line p-3">
          <legend className="px-1 text-xs font-medium text-subtle">Matches</legend>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SelectField
              label="Environment"
              placeholder="any"
              value={match.environment ?? ''}
              options={[
                { value: 'dev', label: 'dev' },
                { value: 'test', label: 'test' },
                { value: 'staging', label: 'staging' },
                { value: 'prod', label: 'prod' },
              ]}
              onChange={(event) => setMatch({ environment: event.target.value })}
            />

            <SelectField
              label="Target kind"
              placeholder="any"
              value={match.targetKind ?? ''}
              options={[
                { value: 'mssql', label: 'SQL Server' },
                { value: 'oracle', label: 'Oracle' },
                { value: 'rest', label: 'REST API' },
              ]}
              onChange={(event) => setMatch({ targetKind: event.target.value })}
            />

            <TextField
              label="Target key"
              placeholder="any"
              value={match.targetKey ?? ''}
              onChange={(event) => setMatch({ targetKey: event.target.value })}
            />

            <SelectField
              label="Statement class"
              placeholder="any"
              value={match.statementClass ?? ''}
              options={[
                { value: 'select', label: 'SELECT' },
                { value: 'insert', label: 'INSERT' },
                { value: 'update', label: 'UPDATE' },
                { value: 'delete', label: 'DELETE' },
                { value: 'merge', label: 'MERGE' },
                { value: 'procedure', label: 'stored procedure' },
              ]}
              onChange={(event) => setMatch({ statementClass: event.target.value })}
            />

            <SelectField
              label="HTTP method"
              placeholder="any"
              value={match.httpMethod ?? ''}
              options={['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => ({
                value: m,
                label: m,
              }))}
              onChange={(event) => setMatch({ httpMethod: event.target.value })}
            />

            <SelectField
              label="Credential"
              placeholder="any"
              value={match.credentialKind ?? ''}
              options={[
                { value: 'read', label: 'read account' },
                { value: 'write', label: 'write account' },
              ]}
              onChange={(event) => setMatch({ credentialKind: event.target.value })}
            />

            <SelectField
              label="Mode"
              placeholder="any"
              value={match.mode ?? ''}
              options={[
                { value: 'interactive', label: 'interactive' },
                { value: 'batch', label: 'batch export' },
              ]}
              onChange={(event) => setMatch({ mode: event.target.value })}
            />

            <TextField
              label="Rows above"
              type="number"
              placeholder="any"
              hint="Only matches when a row estimate exists."
              value={match.rowCountAbove === undefined ? '' : String(match.rowCountAbove)}
              onChange={(event) =>
                setMatch({
                  rowCountAbove: event.target.value === '' ? undefined : Number(event.target.value),
                })
              }
            />
          </div>
        </fieldset>

        <div className="flex flex-wrap gap-3">
          <TextField
            label="Approvers required"
            type="number"
            containerClassName="w-44"
            hint="1 to 5. The requester never counts."
            value={String(rule.requiredApprovals ?? 1)}
            onChange={(event) =>
              onChange({ ...rule, requiredApprovals: Number(event.target.value) })
            }
          />

          <TextField
            label="Expires after (minutes)"
            type="number"
            containerClassName="w-56"
            hint="5 minutes to 7 days. An undecided request closes itself."
            value={String(rule.ttlMinutes ?? 240)}
            onChange={(event) => onChange({ ...rule, ttlMinutes: Number(event.target.value) })}
          />

          <SelectField
            label="Enabled"
            containerClassName="w-36"
            value={rule.enabled === false ? 'no' : 'yes'}
            options={[
              { value: 'yes', label: 'Enabled' },
              { value: 'no', label: 'Disabled' },
            ]}
            onChange={(event) => onChange({ ...rule, enabled: event.target.value === 'yes' })}
          />
        </div>

        {(rule.requiredApprovals ?? 1) < 2 && match.environment === 'prod' ? (
          <Alert tone="warning">
            One approver on a production rule means a single person can release a change. Two is the
            usual expectation for production, and the difference is what an auditor asks about.
          </Alert>
        ) : null}

        <Card tone="info" className="p-2 text-xs">
          Saving records both the previous and the new values in the audit trail. Reducing the
          number of approvers, lengthening the expiry, or disabling a rule additionally raises an
          alert the same day.
        </Card>
      </div>
    </Modal>
  );
}

function formatTtl(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes === 1440 ? '' : 's'}`;
  return `${(minutes / 60).toFixed(minutes % 60 === 0 ? 0 : 1)} h`;
}
